import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, mergeProps as _mergeProps, vShow as _vShow, withDirectives as _withDirectives, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "block w-full" }
const _hoisted_2 = { class: "flex mb-4" }
const _hoisted_3 = { class: "ml-auto" }
const _hoisted_4 = { class: "text-center" }

import {PopupType, ErrorLocationType, TableType} from '@/utilities/enums/Enums';
import { BryntumGrid } from "@bryntum/grid-vue-3";
import {Menu, Store} from "@bryntum/grid";
import GridInputFilter from "@/components/GridInputFilter.vue";
import {onBeforeUnmount, onMounted, reactive, ref, markRaw} from "vue";
import {useGlobalStore} from "@/stores/GlobalStore";
import {$t} from "@/utilities/i18n/config";
import {defaultSpaceAroundTableOnPage} from "@/AppConfig";
import ErrorHandler from "@/utilities/helpers/ErrorHandler";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import Error from "@/components/Error.vue";
import {SettingService} from "@/utilities/services/entities/SettingService";
import {SystemSetting} from "@/models/setting/SystemSetting";
import {GeneralResponse} from "@/models/api/Responses";



export default /*#__PURE__*/_defineComponent({
  __name: 'SystemSettingsTab',
  setup(__props) {

const gridStore = new Store();
const globalStore = useGlobalStore();
const settingService = new SettingService();

const currentWindowHeight = ref("");
const recalcGridHeight = () => {
  currentWindowHeight.value = `${window.innerHeight-defaultSpaceAroundTableOnPage-80}px`;
};

const grid = ref(null);
const useGridConfig = {
  headerMenuFeature: false,
  cellTooltipFeature: {
    hoverDelay: 300,
    textContent: true,
		hideOnDelegateChange: true,
  },
  groupFeature:  "groupName",
  selectionMode: { row: false, cell: false },
  height:currentWindowHeight,
  cellEditFeature: false,
  filterBarFeature: false,
  rowReorderFeature: true,
  stripeFeature: false,
  treeFeature: false,
  columns: {
    data: [
      {
        id: 'name',
        field: 'name',
        text: $t('settings.systemSettings.name'),
        flex: 1,
      },
			{
				id: 'helpText',
				field: 'helpText',
				text: "",
				width: 50,
				sortable: false,
				resizable: false,
				minWidth: 50,
				type: "template",
				tooltipRenderer : ({record}) => `${record.data.helpText}`,
				template: ({record}) => {
					if(record.data.helpText !== null)
						return `<div class='gridTooltipButton'><span class='icon-info'></span></div>`;
					return
				},
			},
      {
        id: 'value',
        field: 'value',
        text: $t('settings.systemSettings.value'),
        flex: 2,
				autoHeight: true,
				cellCls: 'multiline',
        renderer: ({ record }: any) => {
          if (!record.data) return '';

          // Passwort verbergen
          if (record.data.dataControlJavaScript === 'Passwort') {
            return '••••••••'; // oder '********'
          }

          // Booleans übersetzen
          if (record.data.dataType === 'System.Boolean') {
            const val = String(record.data.value).toLowerCase();
            if (val === 'true') {
              return $t('global.activated');
            } else if (val === 'false') {
              return $t('global.deactivated');
            }
          }

          // Wenn es eine Dropdown-Liste ist (PossibleValues), den Namen statt der ID anzeigen
          if (record.data.possibleValues && record.data.possibleValues.length > 0) {
            const selected = record.data.possibleValues.find((v: any) => String(v.id) === String(record.data.value));
            if (selected) {
              return selected.name;
            }
          }

          return record.data.value;
        }
      },
      {
        id: 'groupName',
        field: 'groupName',
        isVisible: false,
        hidden:true,
        text: '',
        groupRenderer : ({groupRowFor}) => {
          return `${groupRowFor}`
        },
      },
      {
        id: 'menu',
        text	: '',
        width   : 36,
        type	: 'widget',
        align: "center",
        hideable: false,
        resizable: false,
        sortable: false,
        filterable: false,
        widgets : [{
          type	 : 'button',
          width: 36,
          icon	 : 'icon-kontext_horizontal',
          onAction : ({ source: btn }) => {
            const menu = menuFactory(btn.cellInfo.record.data);
            menu.alignTo({ target: btn })
            menu.show();
          }
        }]
      },
    ],
    listeners:{
      async add(event:any){
        if(event.records.length === 1 && event.isMove[event.records[0].id]){
          await globalStore.saveUserColumnOrder(TableType[TableType.systemSetting], event.records[0].id, event.index.toString());
        }
      },
    }
  },
}
const gridConfig = reactive(useGridConfig);

const menuFactory = (record:object) => {
  const menu = new Menu({
    anchor   : true,
    autoShow : true,
    items	: [
      {
        text: $t("global.edit"),
        cls: 'color',
        icon: 'icon-editieren',
        weight: 100,
        onItem : ({ menu }) => {
          globalStore.openPopup({
            data: JSON.parse(JSON.stringify(menu.record)),
            action:updateSystemSetting,
            resetButton:false,
            title: $t("popups.settings.systemSettings.edit"),
            type:PopupType.settingsEditSystemSetting
          });
        }
      }
    ],
  });

  menu.setRecord(record);

  return menu;
};

const updateSystemSetting = async ()=>{
  const setting = globalStore.modal.contentObject as SystemSetting;

  if(setting.dataType === "System.Boolean" && !setting.value.match(/[tT]rue|[fF]alse/gm)) // magic string, pfui bah!
  {
    ErrorHandler.handleError(new GeneralResponse(666666666, ""), ErrorLocationType.popup);
    return;
  }

  const updateSystemSettingResp = await settingService.putUpdateSystemSetting(setting);
  if (updateSystemSettingResp === null || updateSystemSettingResp === undefined || updateSystemSettingResp.statusCode !== 0) {
    ErrorHandler.handleError(updateSystemSettingResp, ErrorLocationType.popup);
    return;
  }

  if(setting.name === "Tätigkeitsgruppen: Feature eingeschaltet")
    globalStore.settings.activitySettings.activitesEnabled = JSON.parse(setting.value.toLocaleLowerCase());
  if(setting.name === "Tätigkeitsgruppen:  Unbestimmte Tätigkeit änderbar")
    globalStore.settings.activitySettings.defaultActivitExchangeable = JSON.parse(setting.value.toLocaleLowerCase());

  if (setting.groupName === "AutomatischeAbmeldung" && setting.refKey === "AutomatischeAbmeldungAktiv") {
    globalStore.settings.automaticUnregister = setting.value;
  }
  if (setting.groupName === "AutomatischeAbmeldung" && (setting.refKey === "AutomatischeAbmeldungMindestabstand" || setting.name.includes("Mindestabstand"))) {
    globalStore.settings.automaticUnregisterMinDistance = parseFloat(setting.value) || 0;
  }

  // Statt eine tiefe Kopie per add() blind ins Grid zu pushen (was bei gleichen IDs 
  // Bryntums Virtual DOM zerstört und zu 'Cannot read properties of undefined' führt),
  // updaten wir hier ganz explizit den *existierenden* Record aus dem Store!
  const cleanSetting = JSON.parse(JSON.stringify(setting));
  const record = gridStore.getById(cleanSetting.id);
  if (record) {
    // Überschreibt den Grid-Datensatz sauber mit den neuen Werten aus dem Modal
    record.set(cleanSetting);
  } else {
    // Fallback, sollte normalerweise nicht eintreten, da es eine Edit-Aktion ist
    gridStore.add(cleanSetting);
  }

  globalStore.closeModal();
  globalStore.showSuccess($t("global.saved"));
}

const setGridData = async ()=>{
  // start spinner
  globalStore.gridDataIsLoading = true;

  const systemSettingsResp = await settingService.getSystemSettings();
  if (systemSettingsResp === null || systemSettingsResp === undefined || systemSettingsResp.statusCode !== 0) {
    ErrorHandler.handleError(systemSettingsResp, ErrorLocationType.table);
    return;
  }

  // convert to flat array since treeFeature with 'children prop' is not working...
  const flatGroupableList = [];
  for (const s of systemSettingsResp.settingInfo){
    for (const c of s.children){
      c.groupName = s.name;
      flatGroupableList.push(c)
        if (c.groupName === "AutomatischeAbmeldung") {
            if (c.refKey === "AutomatischeAbmeldungAktiv") {
                globalStore.settings.automaticUnregister = c.value;
            }
        }
    }
  }

  gridStore.setStoreData(flatGroupableList);

  globalStore.reorderColumns(TableType[TableType.systemSetting]);
    
  // stop spinner
  globalStore.gridDataIsLoading = false;
}


onMounted(async () => {
  recalcGridHeight();
  window.addEventListener('resize', recalcGridHeight);

  globalStore.setGridStore(ref(markRaw(gridStore)));
  
  if (grid.value?.instance?.columns) {
    globalStore.setGridColumnStore(ref(markRaw(grid.value.instance.columns)));
  }

  await setGridData();
})

onBeforeUnmount(()=>{
  recalcGridHeight();
  window.removeEventListener('resize', recalcGridHeight);

  globalStore.setGridStore(null);
  globalStore.setGridColumnStore(null);

  globalStore.currentItems = 0;
  globalStore.allItems = 0;
});


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _withDirectives(_createElementVNode("div", null, [
      _createVNode(Error, {
        errorLoc: _unref(ErrorLocationType).table
      }, null, 8, ["errorLoc"]),
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createVNode(GridInputFilter, {
              gridStore: _unref(gridStore),
              hideFilterButton: true
            }, null, 8, ["gridStore"])
          ])
        ]),
        _createVNode(_unref(BryntumGrid), _mergeProps({
          ref_key: "grid",
          ref: grid
        }, gridConfig, {
          store: _unref(gridStore),
          readOnly: true
        }), null, 16, ["store"])
      ])
    ], 512), [
      [_vShow, !_unref(globalStore).gridDataIsLoading]
    ]),
    _withDirectives(_createElementVNode("div", _hoisted_4, [
      _createVNode(LoadingSpinner)
    ], 512), [
      [_vShow, _unref(globalStore).gridDataIsLoading]
    ])
  ], 64))
}
}

})